import { useCallback, useEffect, useState } from "react";
import { fetchAdsActivity, type AdsActivityResponse } from "@/lib/adsAssistantClient";
import { clientLocalCalendarDay, clientLocalDayBounds } from "@/lib/clientLocalDay";

const POLL_MS = 60_000;

export function useAdsHeaderActivity(scopedOrgId: string | null) {
  const [activity, setActivity] = useState<AdsActivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!scopedOrgId) {
      setActivity(null);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const bounds = clientLocalDayBounds();
      const r = await fetchAdsActivity({
        logged_after: bounds.logged_after,
        logged_before: bounds.logged_before,
        local_calendar_day: clientLocalCalendarDay(),
      });
      if (!r.ok) {
        setError(r.error);
        setActivity(null);
      } else {
        setError(null);
        setActivity(r.data);
      }
    } finally {
      setLoading(false);
    }
  }, [scopedOrgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!scopedOrgId) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh, scopedOrgId]);

  useEffect(() => {
    if (!scopedOrgId) return;
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh, scopedOrgId]);

  return { activity, loading, error, refresh };
}
